import { createContext, useContext, useMemo } from "react";

/**
 * Slot-label override context — Phase 8.0.
 *
 * Provider receives the full `fieldOverrides` array from the booking
 * form's auth/data hook. Children call `useSlotLabel(slotKey)` to get
 * a label-resolution function for their sub-labels.
 *
 * Resolution order (highest → lowest):
 *   1. Override row (scope='slot_label', field_key=`${slotKey}::${subKey}`)
 *   2. Inline fallback passed to t()
 */
const SlotLabelCtx = createContext({ map: new Map() });

export function SlotLabelProvider({ overrides, children }) {
  const map = useMemo(() => {
    const m = new Map();
    for (const o of overrides || []) {
      if (o.scope !== "slot_label") continue;
      if (o.is_visible === false) continue;
      if (!o.label) continue;
      m.set(o.field_key, o.label);
    }
    return m;
  }, [overrides]);
  return <SlotLabelCtx.Provider value={{ map }}>{children}</SlotLabelCtx.Provider>;
}

/**
 * Returns a `t(subKey, fallback)` resolver scoped to one slot.
 * Each slot component should call this once near the top of its body.
 */
export function useSlotLabel(slotKey) {
  const { map } = useContext(SlotLabelCtx);
  return (subKey, fallback) => map.get(`${slotKey}::${subKey}`) || fallback;
}
